/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useCallback, useEffect, useState } from 'react';
import _isNil from 'lodash/isNil';
import { Box, Flex, Spinner, useDisclosure } from '@chakra-ui/core';
import { useParams } from 'react-router';
import { Header } from '../../../app/topBar/Header';
import { CandidatesList } from './CandidatesList';
import { FilterParamsType, SelectedTab, useTopCandidatesContext } from './TopCandidatesContext';
import { SeekerProfile } from './SeekerProfile';
import { TopCandidatesSidebarFilter } from './TopCandidatesSidebarFilter';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { PaginatedTopCandidateResponse, TopCandidateDataType, TopCandidatePagination } from './TopCandidateDataType';
import { TopCandidateParams } from '../../../routes/constants';
import { DismissConfirmationModal } from './DismissConfirmationModal';
import { EngagedConfirmationModal } from './EngagedConfirmationModal';
import { TokenView } from '../../tokens/TokenView';
import { NotAuthorizedView } from '../NotAuthorizedView';
import colors from '../../../styles/colors';

export const TopCandidates = (): JSX.Element => {
  const { positionId } = useParams<TopCandidateParams>();
  const { isOpen: isDismissModalOpen, onOpen: onDismissModalOpen, onClose: onDismissModalClose } = useDisclosure();
  const { isOpen: isEnagageModalOpen, onOpen: onEnagageModalOpen, onClose: onEnagageModalClose } = useDisclosure();
  const {
    isOpen: isRechargeTokenModalOpen,
    onOpen: onRechargeTokenModalOpen,
    onClose: onRechargeTokenModalClose,
  } = useDisclosure();
  const accountStore = useStoreState((s) => s.app.accounts);
  const userStore = useStoreState((s) => s.app.user);
  const isOperatorEnabled = useStoreState((s) => s.app.user?.operator?.enabled);

  const { isFilterOpened, tab, setSelectedTopCandidate, selectedTopCandidate, positionDocument } = useTopCandidatesContext();
  const { getPaginatedTopCandidates } = useStoreActions((action) => action.positionManagement);

  const [topCandidates, setTopCandidates] = useState<TopCandidateDataType[]>([]);
  const [isRefreshClicked, setIsRefreshClicked] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagingData, setPagingData] = useState<TopCandidatePagination>({
    total: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [dataCount, setDataCount] = useState<number>(0);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchItem, setSearchItem] = useState<string>('');
  const [filtersData, setFiltersData] = useState<FilterParamsType>({});

  const updateCandidateState = (response: PaginatedTopCandidateResponse, pageValue: number, resetData = false) => {
    if (response?.data) {
      setTopCandidates(response.data);
      setPagingData({
        ...pagingData,
        total: response?.paging?.total,
        hasNextPage: response?.paging?.hasNextPage,
        hasPreviousPage: response?.paging?.hasPreviousPage,
      });
      setSelectedTopCandidate(response.data[0]);
      if (resetData) {
        setDataCount(response?.data?.length);
      } else {
        setDataCount((prevCount) => (prevCount === 0 ? response?.data?.length : prevCount));
      }
    } else {
      setTopCandidates([]);
      setSelectedTopCandidate(undefined);
    }
    setCurrentPage(pageValue);
  };

  const getTopCandidateList = useCallback(
    // eslint-disable-next-line consistent-return
    async (positionID: string, pageValue: number, filtersType?: FilterParamsType, input?: string, reset = false) => {
      const topCandidateBodyData = {
        communityCandidatesCount: 0,
        excludedSeekerIds: [],
        maxItems: tab === SelectedTab.BEST_MATCH ? 50 : 20,
        startFromId: '',
        startFromIndex: 0,
        searchType: tab,
        search: input,
        filters: filtersType,
        page: pageValue,
      };

      try {
        setIsLoading(true);
        const response = await getPaginatedTopCandidates({ positionId: positionID, topCandidateBodyData });
        if (response) {
          setSelectedTopCandidate(response.data[0]);
          updateCandidateState(response, pageValue, reset);
        }
        return response;
      } catch (error) {
        console.error('Error while fetching top candidates', error);
      } finally {
        setIsLoading(false);
        setIsRefreshClicked(false);
        setIsFetching(false);
      }
      // return getPaginatedTopCandidates({ positionId: positionID, topCandidateBodyData });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getPaginatedTopCandidates, tab],
  );

  useEffect(() => {
    async function fetchTopCandidates() {
      setIsLoading(true);
      if (positionDocument) {
        await getTopCandidateList(positionDocument.id, currentPage);
      }
    }
    fetchTopCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionDocument]);

  useEffect(() => {
    if (isRefreshClicked && positionDocument) {
      getTopCandidateList(positionDocument.id, currentPage, filtersData, searchItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshClicked]);

  const handleApplyFilters = async (filters: FilterParamsType) => {
    if (positionDocument) {
      const firstPage = 1;
      await getTopCandidateList(positionDocument.id, firstPage, filters, searchItem, true);
      setFiltersData(filters);
    }
  };

  const handleSearch = async (searchValue: string) => {
    if (positionDocument) {
      const firstPage = 1;
      setCurrentPage(firstPage);
      await getTopCandidateList(positionDocument.id, firstPage, filtersData, searchValue, true);
      setSearchItem(searchValue);
    }
  };

  // handle next page fetching
  const handleNextPage = async () => {
    if (pagingData.hasNextPage && !isFetching && positionDocument) {
      setIsFetching(true);
      const nextPage = currentPage + 1;
      const res = await getTopCandidateList(positionDocument.id, nextPage, filtersData, searchItem);
      if (res) {
        setDataCount((prevCount) => prevCount + res?.data?.length);
      }
    }
  };

  // previous page fetching
  const handlePrevPage = async () => {
    if (pagingData.hasPreviousPage && positionDocument?.id && currentPage > 1) {
      setIsFetching(true);
      const prevPage = currentPage - 1;
      const res = await getTopCandidateList(positionDocument.id, prevPage, filtersData, searchItem);
      if (res) {
        setDataCount((prevCount) => prevCount - topCandidates.length);
      }
    }
  };

  // handling token to engage candidate
  const handleTokenEngageCandidate = () => {
    if (
      !_isNil(accountStore) &&
      accountStore.monetizationEnabled === true &&
      accountStore?.remainingCredits !== undefined &&
      accountStore?.remainingCredits === 0
    ) {
      onRechargeTokenModalOpen();
    } else if (accountStore?.monetizationEnabled && accountStore?.remainingCredits && accountStore?.remainingCredits >= 1) {
      onEnagageModalOpen();
    }
  };

  const handleActionAfterDismiss = (seekerId: string) => {
    const indexOfCurrentCandidate = topCandidates.findIndex((c) => c.seeker.id === seekerId);
    const filteredCandidates = topCandidates.filter((c) => c.seeker.id !== seekerId);
    setTopCandidates(filteredCandidates);

    if (filteredCandidates.length === 0) {
      setSelectedTopCandidate(undefined);
      return;
    }

    let nextIndex = indexOfCurrentCandidate - 1;

    if (nextIndex < 0) {
      nextIndex = 0;
    }

    setSelectedTopCandidate(filteredCandidates[nextIndex]);
  };

  if (positionDocument) {
    const isAccountMatched = positionDocument?.account === userStore?.account;
    const hasNoAccessibleAccounts = isOperatorEnabled && !userStore?.operator?.accessibleAccounts;
    const hasAccountInAccessibleAccounts =
      positionDocument?.account &&
      isOperatorEnabled &&
      userStore?.operator?.accessibleAccounts &&
      userStore?.operator?.accessibleAccounts?.length > 0;
    if (!isAccountMatched && !hasNoAccessibleAccounts && !hasAccountInAccessibleAccounts) {
      return (
        <Box data-testid="NotAuthorizedView">
          <NotAuthorizedView isAuthorizationStateLoaded />
        </Box>
      );
    }
  }

  return (
    <Box position="absolute" height="100vh" width="100vw" margin="0">
      <Header showMenu />
      <Flex height="calc(100vh - 64px)">
        {isFilterOpened && <TopCandidatesSidebarFilter onApply={handleApplyFilters} />}
        <Box
          w={isFilterOpened ? '63%' : '78%'}
          position="relative"
          borderRight={`1px solid ${colors.neutral[200]}`}
          borderLeft={`1px solid ${colors.neutral[200]}`}
        >
          <CandidatesList
            topCandidates={topCandidates}
            isLoading={isLoading}
            isFetching={isFetching}
            setIsRefreshClicked={setIsRefreshClicked}
            totalCount={pagingData.total}
            currentCount={dataCount}
            hasNextData={pagingData.hasNextPage}
            hasPreviousData={pagingData.hasPreviousPage}
            handleNextData={handleNextPage}
            handlePrevData={handlePrevPage}
            onSearch={handleSearch}
          />
        </Box>
        <Box w="28%">
          {isLoading ? (
            <Flex alignItems="center" justifyContent="center" h="100%">
              <Spinner size="md" />
            </Flex>
          ) : (
            <SeekerProfile
              onDismissModalOpen={onDismissModalOpen}
              onEnagageModalOpen={handleTokenEngageCandidate}
              handleActionAfterEngage={handleActionAfterDismiss}
            />
          )}

          {isDismissModalOpen && selectedTopCandidate && positionId && (
            <DismissConfirmationModal
              isOpen={isDismissModalOpen}
              onClose={onDismissModalClose}
              positionId={positionId}
              seekerId={selectedTopCandidate.seeker.id}
              handleActionAfterDismiss={handleActionAfterDismiss}
            />
          )}

          {isEnagageModalOpen && selectedTopCandidate && positionId && (
            <EngagedConfirmationModal
              isOpen={isEnagageModalOpen}
              onClose={onEnagageModalClose}
              positionId={positionId}
              seekerId={selectedTopCandidate.seeker.id}
              handleActionAfterEngage={handleActionAfterDismiss}
            />
          )}

          {isRechargeTokenModalOpen && positionId && (
            <TokenView open={isRechargeTokenModalOpen} onCloseModal={() => onRechargeTokenModalClose()} positionId={positionId} />
          )}
        </Box>
      </Flex>
    </Box>
  );
};
